/**
 * Painter projection: forward-axis compression and selective vertical exaggeration
 * to match Cornelis Anthonisz.'s 1538 bird's-eye view painting.
 *
 * The painting compresses the landscape so that the painted horizon at row 145.1/1454
 * appears where the camera's true horizon would be far above the frame. This module
 * derives the projection parameters from the camera pose and STYLE constants, and
 * provides both TypeScript and GLSL-mirrored warping functions.
 *
 * Key insight: compression must be along the forward axis (not radial) to produce
 * a straight horizon. Points at the same forward distance d share the same image row.
 *
 * Past d0 the far map is the one fitted to the painted far field (STYLE §2): the forward
 * distance runs from d0 toward dH by a short knee and a slow part, and the ground shears right
 * with the slow part, as the painter drew the Overtoom, the Kostverlorenvaart and the Schinkel.
 */

import type { CameraPose } from '../data/types';
import type { Vec3 } from '../geo/types';

export interface PainterProjectionConstants {
  readonly paintedHorizonRow: number;
  readonly imageWidth: number;
  readonly imageHeight: number;
  readonly verticalExaggeration: number;
  readonly slantStartDistanceM: number;
  // The far map past d0: the knee's length, the slow part's length, and the shear the slow part
  // reaches far out, as a tangent of the view's azimuth.
  readonly farKneeM: number;
  readonly farScaleM: number;
  readonly farShear: number;
}

export interface HorizonParams {
  readonly horizonDepressionDeg: number;
  readonly horizonDistanceM: number;
  readonly groundPoint: Vec3;
  readonly cameraHeight: number;
  readonly forwardDir: Vec3;
  readonly rightDir: Vec3;
}

export interface DerivedPainterParams {
  readonly groundPoint: Vec3;
  readonly cameraHeight: number;
  readonly d0: number;
  readonly dH: number;
  readonly L: number;
  readonly verticalExaggeration: number;
  readonly horizonDepressionRad: number;
  readonly forwardDir: Vec3;
  readonly rightDir: Vec3;
  readonly farKneeM: number;
  // The knee's share of the way from d0 to dH, which leaves d0 at slope 1.
  readonly farKneeShare: number;
  readonly farScaleM: number;
  readonly farShear: number;
}

/**
 * Computes the horizon parameters from camera pose and STYLE constants.
 *
 * The painted horizon row 145.1 in a 1454-pixel image corresponds to a specific
 * elevation angle relative to the camera's view direction. This elevation, combined
 * with the camera height, determines the forward horizon distance d_H.
 *
 * The forward direction is derived from the camera's yaw angle (compass bearing).
 */
export function computeHorizonParams(
  camera: CameraPose,
  constants: PainterProjectionConstants
): HorizonParams {
  const pitchRad = computePitchFromPose(camera);
  const yawRad = computeYawFromPose(camera);

  const fovYRad = (camera.fovYDeg * Math.PI) / 180;
  const tanHalfFov = Math.tan(fovYRad / 2);

  const ndcY = 1 - (2 * constants.paintedHorizonRow) / constants.imageHeight;

  const horizonElevationRad = pitchRad + Math.atan(ndcY * tanHalfFov);
  const horizonDepressionRad = -horizonElevationRad;
  const horizonDepressionDeg = (horizonDepressionRad * 180) / Math.PI;

  const cameraHeight = camera.position[1];
  const horizonDistanceM = cameraHeight / Math.tan(horizonDepressionRad);

  const groundPoint: Vec3 = [camera.position[0], 0, camera.position[2]];

  const forwardDir: Vec3 = [Math.sin(yawRad), 0, -Math.cos(yawRad)];
  const rightDir: Vec3 = [Math.cos(yawRad), 0, Math.sin(yawRad)];

  return {
    horizonDepressionDeg,
    horizonDistanceM,
    groundPoint,
    cameraHeight,
    forwardDir,
    rightDir,
  };
}

function computePitchFromPose(camera: CameraPose): number {
  const dx = camera.target[0] - camera.position[0];
  const dy = camera.target[1] - camera.position[1];
  const dz = camera.target[2] - camera.position[2];
  const horizontal = Math.sqrt(dx * dx + dz * dz);
  return Math.atan2(dy, horizontal);
}

function computeYawFromPose(camera: CameraPose): number {
  const dx = camera.target[0] - camera.position[0];
  const dz = camera.target[2] - camera.position[2];
  return Math.atan2(dx, -dz);
}

/**
 * Derives all painter projection parameters from camera pose and constants.
 *
 * d0: the forward distance where compression begins
 * dH: the forward horizon distance (asymptote of the warp)
 * L: dH - d0, the compression length scale
 *
 * The d0 value is set to 92% of dH to ensure city landmarks (within ~3.3km forward)
 * remain unaffected by the warp, while far terrain converges to the horizon.
 */
export function derivePainterProjectionParams(
  camera: CameraPose,
  constants: PainterProjectionConstants
): DerivedPainterParams {
  const horizon = computeHorizonParams(camera, constants);

  const dH = horizon.horizonDistanceM;
  const d0 = dH * 0.92;
  const L = dH - d0;

  const horizonDepressionRad = (horizon.horizonDepressionDeg * Math.PI) / 180;

  return {
    groundPoint: horizon.groundPoint,
    cameraHeight: horizon.cameraHeight,
    d0,
    dH,
    L,
    verticalExaggeration: constants.verticalExaggeration,
    horizonDepressionRad,
    forwardDir: horizon.forwardDir,
    rightDir: horizon.rightDir,
    farKneeM: constants.farKneeM,
    farKneeShare: kneeShare(L, constants.farKneeM, constants.farScaleM),
    farScaleM: constants.farScaleM,
    farShear: constants.farShear,
  };
}

/*
 * The knee's share w of the far map's progress 1 - w e^(-t/knee) - (1 - w) e^(-t/scale) that makes
 * its slope at d0, L (w/knee + (1 - w)/scale), equal the identity's: a share between 0 and 1 needs
 * the knee shorter than L and the slow part longer.
 */
function kneeShare(L: number, kneeM: number, scaleM: number): number {
  if (!(kneeM < L && L < scaleM)) {
    throw new RangeError(
      `The far map needs its knee (${String(kneeM)} m) shorter than L (${String(L)} m) and its slow part (${String(scaleM)} m) longer`
    );
  }
  return (1 / L - 1 / scaleM) / (1 / kneeM - 1 / scaleM);
}

// The share of the way from d0 to dH that the far map carries a point t metres past d0.
function farProgress(t: number, params: DerivedPainterParams): number {
  const w = params.farKneeShare;
  return 1 - w * Math.exp(-t / params.farKneeM) - (1 - w) * Math.exp(-t / params.farScaleM);
}

function farProgressSlope(t: number, params: DerivedPainterParams): number {
  const w = params.farKneeShare;
  return (
    (w / params.farKneeM) * Math.exp(-t / params.farKneeM) +
    ((1 - w) / params.farScaleM) * Math.exp(-t / params.farScaleM)
  );
}

// The tangent of the azimuth the far map adds to a point t metres past d0: the slow part's ramp
// 1 - e^(-t/scale) eased in over the knee, so it leaves d0 flat and no straight road turns there.
function farShearAt(t: number, params: DerivedPainterParams): number {
  const [knee, scale] = [params.farKneeM, params.farScaleM];
  const eased = (scale * Math.exp(-t / scale) - knee * Math.exp(-t / knee)) / (scale - knee);
  return params.farShear * (1 - eased);
}

/** A value with its first and second derivatives. */
export type Jet = readonly [number, number, number];

/**
 * The far map at full amount at forward distance d past d0, with its slopes and curvatures along d:
 * the forward distance it draws the ground at, and the shear (farShearAt).
 */
export function farMapJets(d: number, params: DerivedPainterParams): { forward: Jet; shear: Jet } {
  const t = d - params.d0;
  const w = params.farKneeShare;
  const [knee, scale] = [params.farKneeM, params.farScaleM];
  const [fast, slow] = [Math.exp(-t / knee), Math.exp(-t / scale)];
  const easing = params.farShear / (scale - knee);
  return {
    forward: [
      params.d0 + params.L * farProgress(t, params),
      params.L * farProgressSlope(t, params),
      -params.L * ((w / knee ** 2) * fast + ((1 - w) / scale ** 2) * slow),
    ],
    shear: [farShearAt(t, params), easing * (slow - fast), easing * (fast / knee - slow / scale)],
  };
}

/**
 * Warps a forward distance by the far map.
 *
 * For d <= d0: d' = d (identity)
 * For d > d0: d' = d0 + L * psi(d - d0), psi(t) = 1 - w e^(-t/knee) - (1 - w) e^(-t/scale)
 *
 * This is monotonic, C1 at d0, and asymptotes to dH = d0 + L.
 * The amount parameter blends linearly between identity and full warp.
 */
export function warpForwardDistance(
  d: number,
  params: DerivedPainterParams,
  amount: number
): number {
  if (amount < 0.001) return d;

  const dPrime = d <= params.d0 ? d : params.d0 + params.L * farProgress(d - params.d0, params);

  return d + amount * (dPrime - d);
}

/*
 * Newton on psi(t) = target from a start at or below the root stays below it and climbs to it, psi
 * being increasing and concave. The start drops the knee's term, which only adds to psi. Far out the
 * slow part runs out of precision first: its steps shrink below a nanometre per metre.
 */
const UNWARP_MAX_STEPS = 60;
const UNWARP_TOLERANCE = 1e-15;

/**
 * The forward distance the far map at full amount moved to `dPrime`: itself up to d0, and null from
 * the horizon at dH on, which no ground reaches.
 */
export function unwarpForwardDistance(dPrime: number, params: DerivedPainterParams): number | null {
  if (dPrime <= params.d0) return dPrime;
  if (dPrime >= params.dH) return null;
  const target = (dPrime - params.d0) / params.L;
  const slowShare = 1 - params.farKneeShare;
  let t = Math.max(0, params.farScaleM * Math.log(slowShare / (1 - target)));
  for (let step = 0; step < UNWARP_MAX_STEPS; step++) {
    const miss = target - farProgress(t, params);
    if (miss <= UNWARP_TOLERANCE) break;
    t += miss / farProgressSlope(t, params);
  }
  return params.d0 + t;
}

/**
 * The forward and lateral offsets from the painter's foot that the far map at full amount moved to
 * (dPrime, xPrime), or null from the horizon on.
 */
export function unwarpGroundOffset(
  dPrime: number,
  xPrime: number,
  params: DerivedPainterParams
): [number, number] | null {
  const d = unwarpForwardDistance(dPrime, params);
  if (d === null) return null;
  if (d <= params.d0) return [dPrime, xPrime];
  return [d, (xPrime / dPrime - farShearAt(d - params.d0, params)) * d];
}

/**
 * Warps a world position and normal under the painter projection.
 *
 * Forward-axis compression is applied: points are compressed along the forward
 * direction (determined by camera yaw), with lateral contraction proportional
 * to the compression ratio, and past d0 the far map's shear. This produces a straight horizon.
 *
 * Heights of compressed geometry are scaled by d'/d to maintain angular size.
 *
 * Returns the warped position and transformed normal.
 */
export function warpPoint(
  position: Vec3,
  normal: Vec3,
  params: DerivedPainterParams,
  amount: number
): { position: Vec3; normal: Vec3 } {
  if (amount < 0.001) {
    return { position, normal };
  }

  const dx = position[0] - params.groundPoint[0];
  const dz = position[2] - params.groundPoint[2];
  const d = dx * params.forwardDir[0] + dz * params.forwardDir[2];

  if (d < 0.001) {
    return { position, normal };
  }

  return {
    position: warpPosition(position, params, amount),
    normal: transformNormalUnderWarp(normal, d, params, amount),
  };
}

/**
 * painted.vert's painter offset: a baked vertex the painter drew elsewhere moves by its offset, in
 * world metres, in proportion to the painter amount, before the painter projection reads it.
 */
export function painterOffsetPosition(position: Vec3, offset: Vec3, amount: number): Vec3 {
  return [
    position[0] + offset[0] * amount,
    position[1] + offset[1] * amount,
    position[2] + offset[2] * amount,
  ];
}

/** warpPoint's position alone, for callers that move no normal. */
export function warpPosition(position: Vec3, params: DerivedPainterParams, amount: number): Vec3 {
  if (amount < 0.001) {
    return position;
  }

  const dx = position[0] - params.groundPoint[0];
  const dz = position[2] - params.groundPoint[2];

  const d = dx * params.forwardDir[0] + dz * params.forwardDir[2];
  const x = dx * params.rightDir[0] + dz * params.rightDir[2];

  if (d < 0.001) {
    return position;
  }

  const dPrime = warpForwardDistance(d, params, amount);
  const scale = dPrime / d;
  const shear = d > params.d0 ? amount * farShearAt(d - params.d0, params) : 0;

  const xPrime = x * scale + shear * dPrime;

  const newDx = dPrime * params.forwardDir[0] + xPrime * params.rightDir[0];
  const newDz = dPrime * params.forwardDir[2] + xPrime * params.rightDir[2];

  const heightAboveGround = position[1] - params.groundPoint[1];
  const heightScale = d > params.d0 ? scale : 1;
  const blendedHeightScale = 1 + amount * (heightScale - 1);

  return [
    params.groundPoint[0] + newDx,
    params.groundPoint[1] + heightAboveGround * blendedHeightScale,
    params.groundPoint[2] + newDz,
  ];
}

/**
 * Transforms a normal vector under the forward-axis compression using the Jacobian.
 *
 * The warp has the form:
 *   d' = f(d)      (forward compression)
 *   x' = x * f(d)/d  (lateral contraction)
 *   y' = y * g(d)  (height scaling)
 *
 * The normal transforms as n' = (J^-T) * n, normalized.
 */
function transformNormalUnderWarp(
  normal: Vec3,
  d: number,
  params: DerivedPainterParams,
  amount: number
): Vec3 {
  if (amount < 0.001 || d < 0.001) {
    return normal;
  }

  const dPrime = warpForwardDistance(d, params, 1.0);
  const scale = dPrime / d;
  const ddPrime_dd = d > params.d0 ? params.L * farProgressSlope(d - params.d0, params) : 1;

  const blendedScale = 1 + amount * (scale - 1);
  const blendedDdPrime = 1 + amount * (ddPrime_dd - 1);

  const heightScale = d > params.d0 ? scale : 1;
  const blendedHeightScale = 1 + amount * (heightScale - 1);

  const nForward = normal[0] * params.forwardDir[0] + normal[2] * params.forwardDir[2];
  const nRight = normal[0] * params.rightDir[0] + normal[2] * params.rightDir[2];
  const nY = normal[1];

  const nForwardPrime = nForward / blendedDdPrime;
  const nRightPrime = nRight / blendedScale;
  const nYPrime = nY / blendedHeightScale;

  const nxPrime = nForwardPrime * params.forwardDir[0] + nRightPrime * params.rightDir[0];
  const nzPrime = nForwardPrime * params.forwardDir[2] + nRightPrime * params.rightDir[2];

  const len = Math.sqrt(nxPrime * nxPrime + nYPrime * nYPrime + nzPrime * nzPrime);
  if (len < 0.001) {
    return normal;
  }

  return [nxPrime / len, nYPrime / len, nzPrime / len];
}

/**
 * Creates the GLSL uniforms object for the painter projection.
 * All parameters are computed in TypeScript; GLSL uses them directly.
 */
export function createPainterProjectionUniforms(
  params: DerivedPainterParams
): Record<string, { value: number | [number, number, number] }> {
  return {
    uPainterGroundPoint: {
      value: [params.groundPoint[0], params.groundPoint[1], params.groundPoint[2]],
    },
    uPainterD0: { value: params.d0 },
    uPainterL: { value: params.L },
    uPainterFarKneeM: { value: params.farKneeM },
    uPainterFarKneeShare: { value: params.farKneeShare },
    uPainterFarScaleM: { value: params.farScaleM },
    uPainterFarShear: { value: params.farShear },
    uVerticalExaggeration: { value: params.verticalExaggeration },
    uPainterCameraHeight: { value: params.cameraHeight },
    uPainterForwardDir: {
      value: [params.forwardDir[0], params.forwardDir[1], params.forwardDir[2]],
    },
    uPainterRightDir: {
      value: [params.rightDir[0], params.rightDir[1], params.rightDir[2]],
    },
  };
}

/* The far map's knee is the shortest that draws the ditches crossing d0 straight through it in the
   painter view; its slow part and shear were then fitted to the painted Overtoom junction,
   Kostverlorenvaart, Schinkel, the Overtoom road on the painted vaart and the row of the mills
   beyond the Spui's bridge: 25.2 px RMS, 32.0 px leaving each out (STYLE §2). */
export const STYLE_CONSTANTS: PainterProjectionConstants = {
  paintedHorizonRow: 145.1,
  imageWidth: 2000,
  imageHeight: 1454,
  verticalExaggeration: 3.54,
  slantStartDistanceM: 3460,
  farKneeM: 95,
  farScaleM: 7370.8,
  farShear: 0.4824,
};
